import imageCompression from 'browser-image-compression';

type CompressOptions = {
  maxSizeMB?: number;
  maxWidthOrHeight?: number;
  initialQuality?: number;
};

const AVATAR_DEFAULTS: CompressOptions = {
  maxSizeMB: 0.8,
  maxWidthOrHeight: 512,
  initialQuality: 0.92,
};

const DRAWING_DEFAULTS: CompressOptions = {
  maxSizeMB: 0.55,
  maxWidthOrHeight: 800,
  initialQuality: 0.88,
};

export const MAX_DRAWING_DATA_URL_LENGTH = 900_000;

async function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('Не удалось прочитать изображение'));
    reader.readAsDataURL(blob);
  });
}

async function compressFile(file: File, options: CompressOptions): Promise<File> {
  try {
    return await imageCompression(file, {
      maxSizeMB: options.maxSizeMB ?? 1,
      maxWidthOrHeight: options.maxWidthOrHeight ?? 1280,
      initialQuality: options.initialQuality ?? 0.92,
      useWebWorker: true,
      preserveExif: false,
      fileType: 'image/webp',
    });
  } catch {
    return imageCompression(file, {
      maxSizeMB: options.maxSizeMB ?? 1,
      maxWidthOrHeight: options.maxWidthOrHeight ?? 1280,
      initialQuality: options.initialQuality ?? 0.92,
      useWebWorker: true,
      preserveExif: false,
    });
  }
}

function encodeCanvas(canvas: HTMLCanvasElement, quality: number): string {
  const webp = canvas.toDataURL('image/webp', quality);
  if (webp.startsWith('data:image/webp')) {
    return webp;
  }
  return canvas.toDataURL('image/jpeg', quality);
}

/** Fast canvas resize/compress — works reliably in Safari (no fetch on data URLs). */
export async function compressDataUrl(
  dataUrl: string,
  options: CompressOptions = DRAWING_DEFAULTS,
): Promise<string> {
  if (!dataUrl.startsWith('data:image/')) {
    return dataUrl;
  }

  const maxSide = options.maxWidthOrHeight ?? 800;
  const maxBytes = Math.floor((options.maxSizeMB ?? 0.55) * 1024 * 1024);
  let quality = options.initialQuality ?? 0.88;

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxSide / Math.max(img.width, img.height, 1));
      const width = Math.max(1, Math.round(img.width * scale));
      const height = Math.max(1, Math.round(img.height * scale));

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Не удалось сжать рисунок'));
        return;
      }

      ctx.fillStyle = '#fff7fb';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);

      let result = encodeCanvas(canvas, quality);
      while (result.length > maxBytes && quality > 0.45) {
        quality -= 0.07;
        result = encodeCanvas(canvas, quality);
      }

      if (result.length > MAX_DRAWING_DATA_URL_LENGTH) {
        reject(new Error('Рисунок слишком детальный — упрости или очисти холст'));
        return;
      }

      resolve(result);
    };
    img.onerror = () => reject(new Error('Не удалось обработать рисунок'));
    img.src = dataUrl;
  });
}

export async function compressImageFile(
  file: File,
  options: CompressOptions = AVATAR_DEFAULTS,
): Promise<string> {
  if (!file.type.startsWith('image/')) {
    throw new Error('Выберите изображение');
  }

  const compressed = await compressFile(file, options);
  return blobToDataUrl(compressed);
}

export { AVATAR_DEFAULTS, DRAWING_DEFAULTS };
